import React, { FC } from 'react';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { COLORS } from '../../../app/common/constants/styles';
import Icon from '../../../app/common/components/Icon';
import ICONS from '../../../app/common/constants/icons';
import { getReAuthUrl as importedGetReAuthUrl } from '../../../utils/url';
import SocialProviderItem from '../../../app/common/components/SocialProviderItem';
import { SocialProviderName } from '../../socialAccounts/types/socialAccounts.types';

const SocialAccountsContainer = styled.div`
    margin-top: 10px;
    margin-bottom: 1rem;
    margin-right: 30px;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    border-top: 1px solid ${COLORS.lightGrey};
    border-bottom: 1px solid ${COLORS.lightGrey};
`;

const SocialAccountsTitle = styled.div`
    font-weight: 600;
    font-style: normal;
    font-size: 14px;
    line-height: 100%;
    color: ${COLORS.dark};
    margin-bottom: 1.5rem;
`;

const SocialProvidersList = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 3rem;
    width: 100%;
    flex-wrap: wrap;

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 28px;
        justify-content: flex-start;
        height: auto;
    }
`;

const SocialUnlinkDisabledInfo = styled.div`
    display: flex;
    background-color: #ebf7fe;
    border: 1px solid #317da8;
    color: #317da8;
    border-radius: 4px;
    padding: 16px;
    gap: 16px;
    font-size: 14px;
    margin-top: 16px;
`;

const Link = styled.a`
    color: ${COLORS.linkBlue};
    transition: font-weight 100ms ease-in;
    text-decoration: underline;
    &:hover {
        font-weight: bold;
    }
`;

const PROVIDER_ICONS: Record<string, string> = {
    google: '/assets/logo/logo_google.svg',
    facebook: '/assets/logo/logo_facebook.svg',
    apple: '/assets/logo/logo_apple.svg',
};

export interface SocialProvider {
    socialId: string;
    email: string;
    providerId: string;
}

export interface SocialAccountsSectionProps {
    socialAccounts:
        | {
              google?: SocialProvider;
              facebook?: SocialProvider;
              apple?: SocialProvider;
          }
        | null
        | undefined;
    onUnlinkProvider: (providerId: string, providerName: string) => void;
    appConfig: {
        SOCIAL_ACCOUNT_MANAGEMENT_ENABLED?: boolean;
    };
    disabled?: boolean;
    getReAuthUrl?: () => URL;
}

type ProviderConfig = {
    key: string;
    name: SocialProviderName;
    data: SocialProvider | undefined;
};

const SocialAccountsSection: FC<SocialAccountsSectionProps> = ({
    socialAccounts,
    onUnlinkProvider,
    appConfig,
    disabled = false,
    // 👇 This way it can be stubbed in tests
    getReAuthUrl = importedGetReAuthUrl,
}) => {
    if (!appConfig.SOCIAL_ACCOUNT_MANAGEMENT_ENABLED || !socialAccounts) {
        return null;
    }

    const providers: ProviderConfig[] = [
        {
            key: 'google',
            name: 'Google',
            data: socialAccounts.google,
        },
        {
            key: 'facebook',
            name: 'Facebook',
            data: socialAccounts.facebook,
        },
        {
            key: 'apple',
            name: 'Apple',
            data: socialAccounts.apple,
        },
    ];

    const linkedProviders = providers.filter((provider) => provider.data);

    if (linkedProviders.length === 0) {
        return null;
    }

    const reAuthUrl = getReAuthUrl().toString();

    return (
        <SocialAccountsContainer data-qa-id="social-accounts-section">
            <SocialAccountsTitle>
                {TranslationHelpers.getFormattedMessage('social.accounts.title')}
            </SocialAccountsTitle>
            <SocialProvidersList>
                {linkedProviders.map((provider) => (
                    <SocialProviderItem
                        key={provider.key}
                        providerName={provider.name}
                        providerKey={provider.key}
                        iconSrc={PROVIDER_ICONS[provider.key]}
                        onUnlink={() =>
                            onUnlinkProvider(
                                (provider.data as SocialProvider).providerId,
                                provider.name,
                            )
                        }
                        disabled={disabled}
                    />
                ))}
            </SocialProvidersList>
            {disabled && (
                <SocialUnlinkDisabledInfo data-qa-id="social-unlink-disabled-info">
                    <Icon icon={ICONS.INFO} />
                    <span>
                        {TranslationHelpers.getFormattedMessage('social.unlink.notice', {
                            reAuthLink: (
                                <Link href={reAuthUrl.toString()} target="_self">
                                    {TranslationHelpers.getFormattedMessage(
                                        'social.unlink.notice.link',
                                    )}
                                </Link>
                            ),
                        })}
                    </span>
                </SocialUnlinkDisabledInfo>
            )}
        </SocialAccountsContainer>
    );
};

export default SocialAccountsSection;
